/**
 * One-at-a-time guided question card — first-time answering (Edit is inline on Answered list).
 */
import { useEffect, useRef, useState } from 'react'
import SavedBadge from '../../../../components/SavedBadge'
import { formatMoney, parseMoneyInput } from '../../../../lib/formatters'
import type { GuidedQuestion } from '../../../../lib/costsMarginQuestions'
import { questionProgressLabel } from '../../../../lib/costsMarginQuestions'
import type { CostsMarginPayload } from '../../../../api/costsMargin'
import { useSpeechDictation } from '../../../../hooks/useSpeechDictation'
import GuidedAnswerBody from './GuidedAnswerBody'
import { saveEnabledForMoney } from './MoneyWordsConfirm'
import { saveGuidedDetail, saveGuidedPrimary } from './guidedSave'
import type { QuestionSavePayload } from './types'
import { localDateKey } from '../../../../lib/localDate'
export type { QuestionSavePayload }

type Prefill = {
  amount?: string; text?: string; desc?: string; vendor?: string; category?: string
  projectVendorId?: number; linkedChangeOrderId?: number | null
}

type Props = {
  question: GuidedQuestion; index: number; total: number
  margin: CostsMarginPayload | undefined; savedFlash: boolean; busy: boolean
  prefill?: Prefill; onSkip: () => void; onNext: () => void
  onJumpIndex: (index: number) => void
  onSave: (payload: QuestionSavePayload) => Promise<void>
  onOpenChangeOrders?: () => void
}

export default function GuidedQuestionCard(props: Props) {
  const {
    question, index, total, margin, savedFlash, busy, prefill,
    onSkip, onNext, onJumpIndex, onSave, onOpenChangeOrders,
  } = props
  const [amount, setAmount] = useState('')
  const [text, setText] = useState('')
  const [chip, setChip] = useState('')
  const [showDetail, setShowDetail] = useState(false)
  const [vendor, setVendor] = useState('')
  const [projectVendorId, setProjectVendorId] = useState<number | ''>('')
  const [linkedOwnerCoId, setLinkedOwnerCoId] = useState<number | ''>('')
  const [due, setDue] = useState(localDateKey())
  const [paid, setPaid] = useState(false)
  const [desc, setDesc] = useState('')
  const [category, setCategory] = useState('materials')
  const [wordsOk, setWordsOk] = useState(false)
  const cardRef = useRef<HTMLElement | null>(null)
  const didMount = useRef(false)

  const moneyKinds = question.kind === 'subcontract_amount' || question.kind === 'labor' || question.kind === 'overhead'
  const speech = useSpeechDictation({
    onFinalText: (t) => { if (moneyKinds) setAmount(t); else setText(t); setWordsOk(false) },
    onInterim: () => {},
    onListeningChange: () => {},
  })

  useEffect(() => {
    setAmount(prefill?.amount ?? ''); setText(prefill?.text ?? ''); setDesc(prefill?.desc ?? '')
    setVendor(prefill?.vendor ?? ''); setCategory(prefill?.category ?? 'materials')
    setProjectVendorId(prefill?.projectVendorId ?? ''); setLinkedOwnerCoId(prefill?.linkedChangeOrderId ?? '')
    setShowDetail(false); setChip(''); setDue(localDateKey()); setPaid(false); setWordsOk(false)
  }, [question.id, prefill?.amount, prefill?.text, prefill?.desc, prefill?.vendor, prefill?.category, prefill?.projectVendorId, prefill?.linkedChangeOrderId])

  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }
    const card = cardRef.current
    card?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    const input = card?.querySelector<HTMLInputElement>('[data-testid="cm-answer-input"]')
    input?.focus()
  }, [question.id])

  const original = margin?.original_contract
  const revised = margin?.revised_contract
  const approvedCosCents = (revised?.amount_cents ?? 0) - (original?.amount_cents ?? 0)
  const ownerCos = (margin?.change_orders?.owner ?? []).filter((co) => co.status !== 'void' && co.status !== 'voided')
  const subOptions = (margin?.commitments ?? []).map((c) => ({
    id: c.project_vendor_id,
    label: c.company_name || c.trade || `Sub #${c.project_vendor_id}`,
  }))
  const dollars = parseMoneyInput(amount)
  const primaryEnabled = !busy && (
    question.kind === 'other_costs' || question.kind === 'sub_change_orders'
      ? true
      : saveEnabledForMoney(dollars, wordsOk)
  )

  function resetDetail() {
    setShowDetail(false); setText(''); setAmount(''); setDesc(''); setVendor(''); setDue(localDateKey()); setPaid(false)
    setProjectVendorId(''); setLinkedOwnerCoId(''); setWordsOk(false)
  }

  return (
    <section ref={cardRef} className="cm-card cm-question-card" data-testid="cm-guided-question">
      <div className="cm-card-head">
        <div className="cm-question-top">
          <span className="cm-question-count" data-testid="cm-question-count">
            {questionProgressLabel(index, total, question.phaseLabel, { kind: question.kind })}
          </span>
          {savedFlash ? <SavedBadge testId="cm-saved-badge" /> : <span className="cm-saved-hint">Saved as you go</span>}
        </div>
        <div className="cm-progress" role="tablist" aria-label="Question progress">
          {Array.from({ length: total }, (_, i) => (
            <button
              key={i} type="button" role="tab" aria-selected={i === index}
              aria-label={`Go to question ${i + 1}`}
              className={`cm-progress-seg${i < index ? ' done' : ''}${i === index ? ' current' : ''}`}
              data-testid={`cm-progress-seg-${i}`}
              onClick={() => onJumpIndex(i)}
            />
          ))}
        </div>
      </div>
      <div className="cm-question">
        <h3>{question.title}</h3>
        <p>{question.body}</p>
        {question.kind === 'contract' ? (
          <div className="cm-contract-readonly" data-testid="cm-contract-readonly">
            <div className="cm-math-row"><span>Original</span><strong className="money">{original?.formatted ?? formatMoney(0)}</strong></div>
            <div className="cm-math-row"><span>Approved owner COs</span><strong className="money">{formatMoney(approvedCosCents / 100)}</strong></div>
            <div className="cm-math-row"><span>Revised</span><strong className="money">{revised?.formatted ?? formatMoney(0)}</strong></div>
            {onOpenChangeOrders && (
              <button type="button" className="cm-text-btn" onClick={onOpenChangeOrders}>View change orders</button>
            )}
          </div>
        ) : (
          <GuidedAnswerBody
            question={question}
            amount={amount} setAmount={setAmount}
            text={text} setText={setText}
            chip={chip} setChip={setChip}
            setCategory={setCategory}
            showDetail={showDetail}
            wordsOk={wordsOk} setWordsOk={setWordsOk}
            moneyKinds={moneyKinds}
            primaryEnabled={primaryEnabled}
            busy={busy}
            listening={speech.listening}
            speechOk={speech.speechOk}
            onMicToggle={() => speech.toggle(moneyKinds ? amount : text)}
            onSkip={onSkip}
            onPrimary={() => void saveGuidedPrimary({
              question, amount, text, chip, wordsOk, onNext, onSkip, onSave,
              onShowDetail: () => setShowDetail(true),
              setDescFromText: () => { if (text.trim()) setDesc(text.trim()) },
              clearMoney: () => { setAmount(''); setText(''); setWordsOk(false) },
            })}
            detailProps={{
              desc, setDesc,
              amount, setAmount: (v) => { setAmount(v); setWordsOk(false) },
              vendor, setVendor, due, setDue, paid, setPaid,
              category, setCategory, projectVendorId, setProjectVendorId,
              linkedOwnerCoId, setLinkedOwnerCoId, subOptions,
              ownerCos: ownerCos.map((co) => ({ id: co.id, label: `CO #${co.co_number} · ${co.description}` })),
              onSave: () => void saveGuidedDetail({
                question, amount, text, desc, vendor, category, chip, wordsOk, paid, due,
                projectVendorId, linkedOwnerCoId, subOptions, onSave,
              }).then((ok) => { if (ok) resetDetail() }),
            }}
          />
        )}
        <div className="cm-question-foot">
          {question.kind !== 'contract' && (
            <button type="button" className="cm-text-btn" onClick={onSkip} data-testid="cm-skip">Skip this question</button>
          )}
          <span className="cm-flow-note"><b>Cash forecast:</b> unpaid costs with a due date flow in automatically.</span>
        </div>
      </div>
    </section>
  )
}
